import { cloneElement, useEffect } from 'react';
import Helmet from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import type { Location } from 'history';
import type { ReactElement, VoidFunctionComponent } from 'react';
import type { RouteComponentProps } from 'react-router';

import { useReleaseGroup } from '../../../hooks/api/releaseGroups/useReleaseGroup';
import { useReleaseGroupRelease } from '../../../hooks/api/releaseGroups/useReleaseGroupRelease';
import NavigationStore from '../../../redux/navigation';
import ProjectGroupModule from '../../../redux/project_grouping';
import { useSelectedReleaseId } from '../../../redux/project_grouping/hooks';
import { toFiniteNumber } from '../../../utils';
import Alert, { ALERT_KIND } from '../../generic/Alert';
import Loader from '../../generic/Loader';
import { DEFAULT_PAGE } from '../../generic/Pagination/consts';
import useReleases from '../../shared/releaseGroups/hooks/useReleases';

import ProjectGroupNav from './ProjectGroupNav';
import ReleaseGroupSubnav from './subnav';

type Props = RouteComponentProps<{ projectGroupId: string }, {}> & {
  location: Location<{ releaseScanId?: string }>;
  children: ReactElement;
};

//  Child routes that work without a selected release, so they stay reachable when a release group has no releases.
const RELEASE_INDEPENDENT_ROUTE_PATHS = new Set(['releases', 'releases/:releaseId', 'settings']);

/**
 * Loads a release group and its selected release, and passes them down to the release group pages.
 */
const ProjectGroupContainer: VoidFunctionComponent<Props> = ({ params, location, route, routes, children }) => {
  const dispatch = useDispatch();
  const groupId = Number(params.projectGroupId);

  const { data: projectGroup, isError: isReleaseGroupError } = useReleaseGroup(groupId);
  //  The releases endpoint is ordered newest first, so the first release on the first page is the latest one.
  const { data: latestReleasePage, isError: isLatestReleaseError } = useReleases(params.projectGroupId, DEFAULT_PAGE, 1);
  const latestRelease = latestReleasePage?.releases[0];

  //  A stored selection from another release group is ignored until this group's latest release is selected.
  const selectedReleaseId = useSelectedReleaseId(groupId);
  const isSelectionForThisGroup = selectedReleaseId !== null;
  const selectedScanId: number | null = useSelector(ProjectGroupModule.selectors.getSelectedScanId);
  const releaseSummary = useSelector(ProjectGroupModule.selectors.getReleaseSummary);
  const releaseSummaryStatus = useSelector(ProjectGroupModule.selectors.getReleaseSummaryStatus);
  const scanIdFromUrl = toFiniteNumber(location.query.releaseScanId);

  useEffect(() => {
    if (!isSelectionForThisGroup) {
      if (!latestRelease) {
        return;
      }
      dispatch(ProjectGroupModule.actions.selectRelease(groupId, latestRelease.id));
    }
    //  Also applied when the group is already selected, e.g. after navigating back to an earlier scan's URL.
    if (scanIdFromUrl) {
      dispatch(ProjectGroupModule.actions.selectScan(scanIdFromUrl));
    }
  }, [dispatch, groupId, isSelectionForThisGroup, latestRelease, scanIdFromUrl]);

  //  Start from the latest release again the next time a release group page is opened.
  useEffect(() => () => {
    dispatch(ProjectGroupModule.actions.clearReleaseSelection());
  }, [dispatch]);

  //  This is undefined while a newly selected release loads.
  const { data: selectedRelease, isError: isSelectedReleaseError } = useReleaseGroupRelease(groupId, selectedReleaseId);

  useEffect(() => {
    if (selectedReleaseId) {
      dispatch(ProjectGroupModule.actions.fetchReleaseSummary(groupId, selectedReleaseId));
    }
  }, [dispatch, groupId, selectedReleaseId]);

  useEffect(() => {
    if (!projectGroup) {
      return;
    }
    dispatch(NavigationStore.actions.setNavState({
      expand: 'project-expanded',
      subnav: ProjectGroupNav,
      navProps: {
        projectGroup,
        projectGroupId: groupId,
        //  This ensures that the call to action is shown on the project group page
        shouldUseCallToAction: true,
      },
    }));
  }, [dispatch, groupId, projectGroup]);

  if (isReleaseGroupError || isLatestReleaseError) {
    return (
      <div className="container margin-top">
        <Alert kind={ALERT_KIND.ERROR} header="This release group could not be loaded">
          Reload the page to try again.
        </Alert>
      </div>
    );
  }
  const hasNoReleases = latestReleasePage?.total === 0;
  if (!projectGroup || !latestReleasePage || (!hasNoReleases && !selectedReleaseId)) {
    return <Loader />;
  }

  const childRoutePath = routes[routes.indexOf(route) + 1]?.path;
  const doesPageNeedRelease = !RELEASE_INDEPENDENT_ROUTE_PATHS.has(childRoutePath ?? '');

  let page: ReactElement;
  if (doesPageNeedRelease && hasNoReleases) {
    page = (
      <div className="container margin-top">
        <Alert kind={ALERT_KIND.WARNING} header="This release group has no releases">
          Add a release with the FOSSA CLI or the API.
        </Alert>
      </div>
    );
  } else if (doesPageNeedRelease && isSelectedReleaseError && !selectedRelease) {
    //  A failed background refetch keeps the previously loaded release, so only a release that never loaded is an error.
    page = (
      <div className="container margin-top">
        <Alert kind={ALERT_KIND.ERROR} header="This release could not be loaded">
          Reload the page, or select another release.
        </Alert>
      </div>
    );
  } else {
    page = cloneElement(children, {
      projectGroup,
      projectGroupRelease: selectedRelease,
      scanScope: selectedReleaseId
        ? {
          type: 'releaseGroup',
          groupId: projectGroup.id,
          releaseId: selectedReleaseId,
          scanId: selectedScanId ?? undefined,
        }
        : undefined,
    });
  }

  return (
    <div>
      <Helmet title={`${projectGroup.title} - Release Groups`} />
      <ReleaseGroupSubnav
        projectGroupId={projectGroup.id}
        projectCount={releaseSummary.project_count}
        licenseCount={releaseSummary.license_count}
        releasesCount={latestReleasePage.total}
        isLoadingSummary={releaseSummaryStatus.loading}
        currentReleaseId={selectedReleaseId ?? undefined}
      />
      {page}
    </div>
  );
};

export default ProjectGroupContainer;
